import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Trophy, Plus, TrendingUp, ChevronDown, ChevronUp, Trash2, Edit } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import PRCelebration from './PRCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isCoachRole = (role) => role === 'coach' || role === 'head_coach';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTime = (totalSecs) => {
  const m = Math.floor(totalSecs / 60);
  const s = Math.round(totalSecs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatValue = (log, unit) => {
  const value = typeof log === 'object' ? (log?.value ?? 0) : log;
  const scoreSubtype = typeof log === 'object' ? log?.score_subtype : null;
  const rounds = typeof log === 'object' ? log?.rounds : null;
  const extraReps = typeof log === 'object' ? log?.extra_reps : null;
  const tiebreakSecs = typeof log === 'object' ? log?.tiebreak_seconds : null;

  if (unit === 'for_time') {
    if (scoreSubtype === 'capped') {
      const reps = extraReps ?? 0;
      const tb = tiebreakSecs ? ` + ${formatTime(tiebreakSecs)} TB` : '';
      return `${reps} reps (cap)${tb}`;
    }
    return formatTime(value);
  }

  if (unit === 'amrap') {
    const r = rounds ?? Math.floor(value / 10000);
    const e = extraReps ?? (value % 10000);
    return `${r} rds + ${e} reps`;
  }

  if (unit === 'seconds') {
    const mins = Math.floor(value / 60);
    const secs = Math.round(value % 60);
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  }
  if (unit === 'minutes') return `${value} min`;
  return `${value} ${unit}`;
};

// ---------------------------------------------------------------------------
// Small reusable widgets
// ---------------------------------------------------------------------------

function RxToggle({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {['Rx', 'Scaled'].map((opt) => {
        const active = value ? opt === 'Rx' : opt === 'Scaled';
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt === 'Rx')}
            className={`flex-1 h-9 uppercase text-xs font-bold rounded-sm border transition-colors ${
              active
                ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function CompletionToggle({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {[
        { key: 'completed', label: 'Completed' },
        { key: 'capped',    label: 'Capped (time ran out)' },
      ].map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`flex-1 h-9 text-xs font-bold rounded-sm border transition-colors ${
              active
                ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function TimeInput({ minsValue, secsValue, onMinsChange, onSecsChange, label = 'TIME', required = true }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number" min="0" step="1"
          value={minsValue} onChange={(e) => onMinsChange(e.target.value)}
          placeholder="MM" required={required}
          className="bg-[#09090B] border-[#27272A] text-white text-center"
        />
        <span className="text-[#A1A1AA] font-bold text-xl">:</span>
        <Input
          type="number" min="0" max="59" step="1"
          value={secsValue} onChange={(e) => onSecsChange(e.target.value)}
          placeholder="SS" required={required}
          className="bg-[#09090B] border-[#27272A] text-white text-center"
        />
      </div>
      <p className="text-xs text-[#52525B]">Minutes : Seconds</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dynamic score input section for Log PR dialog
// ---------------------------------------------------------------------------
function PRScoreInput({ unit, form, upd }) {
  if (unit === 'for_time') {
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Completion</Label>
          <CompletionToggle value={form.scoreSubtype} onChange={(v) => upd('scoreSubtype', v)} />
        </div>
        {form.scoreSubtype === 'completed' ? (
          <TimeInput
            label="Time"
            minsValue={form.mins} secsValue={form.secs}
            onMinsChange={(v) => upd('mins', v)} onSecsChange={(v) => upd('secs', v)}
          />
        ) : (
          <>
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Reps Completed</Label>
              <Input
                type="number" min="0" step="1"
                value={form.repsCompleted}
                onChange={(e) => upd('repsCompleted', e.target.value)}
                placeholder="e.g. 87" required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <TimeInput
              label="Tiebreak Time (optional)"
              required={false}
              minsValue={form.tiebreakMins} secsValue={form.tiebreakSecs}
              onMinsChange={(v) => upd('tiebreakMins', v)} onSecsChange={(v) => upd('tiebreakSecs', v)}
            />
          </>
        )}
      </div>
    );
  }

  if (unit === 'amrap') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Rounds</Label>
            <Input
              type="number" min="0" step="1"
              value={form.rounds}
              onChange={(e) => upd('rounds', e.target.value)}
              placeholder="e.g. 5" required
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Extra Reps</Label>
            <Input
              type="number" min="0" step="1"
              value={form.extraReps}
              onChange={(e) => upd('extraReps', e.target.value)}
              placeholder="e.g. 12" required
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
        </div>
      </div>
    );
  }

  if (unit === 'seconds') {
    return (
      <TimeInput
        label="Time"
        minsValue={form.mins} secsValue={form.secs}
        onMinsChange={(v) => upd('mins', v)} onSecsChange={(v) => upd('secs', v)}
      />
    );
  }

  // weight, reps, meters, calories, minutes, lbs, kg — generic numeric
  return (
    <div className="space-y-1.5">
      <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">
        Value ({unit})
      </Label>
      <Input
        type="number" step="any" min="0"
        value={form.value}
        onChange={(e) => upd('value', e.target.value)}
        placeholder="e.g. 100"
        required
        className="bg-[#09090B] border-[#27272A] text-white"
        data-testid="pr-value-input"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build the numeric "value" that the backend stores + sorts on
// ---------------------------------------------------------------------------
const computePRValue = (form, unit) => {
  const toSecs = (m, s) => parseInt(m || 0) * 60 + parseInt(s || 0);

  if (unit === 'for_time') {
    if (form.scoreSubtype === 'capped') return 999999; // never beats a completed time
    return toSecs(form.mins, form.secs);
  }
  if (unit === 'amrap') {
    return parseInt(form.rounds || 0) * 10000 + parseInt(form.extraReps || 0);
  }
  if (unit === 'seconds') return toSecs(form.mins, form.secs);
  return parseFloat(form.value || 0);
};

const buildPRMetadata = (form, unit) => {
  const toSecs = (m, s) => parseInt(m || 0) * 60 + parseInt(s || 0);
  if (unit === 'for_time') {
    const tb = toSecs(form.tiebreakMins, form.tiebreakSecs);
    return {
      score_subtype: form.scoreSubtype,
      tiebreak_seconds: form.scoreSubtype === 'capped' && tb > 0 ? tb : null,
      extra_reps: form.scoreSubtype === 'capped' ? parseInt(form.repsCompleted || 0) : null,
      rounds: null,
    };
  }
  if (unit === 'amrap') {
    return {
      score_subtype: null,
      tiebreak_seconds: null,
      rounds: parseInt(form.rounds || 0),
      extra_reps: parseInt(form.extraReps || 0),
    };
  }
  return { score_subtype: null, tiebreak_seconds: null, rounds: null, extra_reps: null };
};

const emptyLogForm = (movements) => ({
  movement_id: movements[0]?.id || '',
  scoreSubtype: 'completed',
  mins: '', secs: '',
  repsCompleted: '',
  tiebreakMins: '', tiebreakSecs: '',
  rounds: '', extraReps: '',
  value: '',
  rx: true,
  notes: '',
});

// ---------------------------------------------------------------------------
// PRTracking component
// ---------------------------------------------------------------------------

export default function PRTracking({ user }) {
  const [movements, setMovements] = useState([]);
  const [myPRs, setMyPRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMovement, setExpandedMovement] = useState(null);
  const [showLogForm, setShowLogForm] = useState(false);
  const [logFormData, setLogFormData] = useState({ movement_id: '', scoreSubtype: 'completed', mins: '', secs: '', repsCompleted: '', tiebreakMins: '', tiebreakSecs: '', rounds: '', extraReps: '', value: '', rx: true, notes: '' });
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [movementFormData, setMovementFormData] = useState({ name: '', unit: 'kg', lower_is_better: false });
  const [editMovementId, setEditMovementId] = useState(null);
  const [editMovementName, setEditMovementName] = useState('');
  const [celebration, setCelebration] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [movRes, prRes] = await Promise.all([
        axios.get(`${API}/movements`),
        axios.get(`${API}/pr-logs/my-prs`)
      ]);
      setMovements(movRes.data);
      setMyPRs(prRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updLogField = (key, val) => setLogFormData((f) => ({ ...f, [key]: val }));

  const openLogDialog = (movId) => {
    setLogFormData({ movement_id: movId, scoreSubtype: 'completed', mins: '', secs: '', repsCompleted: '', tiebreakMins: '', tiebreakSecs: '', rounds: '', extraReps: '', value: '', rx: true, notes: '' });
    setShowLogForm(true);
  };

  const logPR = async (e) => {
    e.preventDefault();
    const mov = movements.find((m) => m.id === logFormData.movement_id);
    if (!mov) return;

    const value = computePRValue(logFormData, mov.unit);
    const meta = buildPRMetadata(logFormData, mov.unit);

    try {
      const result = await axios.post(`${API}/pr-logs`, {
        movement_id: logFormData.movement_id,
        value,
        notes: logFormData.notes || null,
        rx: logFormData.rx,
        ...meta,
      });
      setShowLogForm(false);
      fetchData();
      if (result.data.beat_existing_pr) {
        setCelebration({ movementName: mov.name, value, unit: mov.unit });
      } else if (result.data.is_pr) {
        toast.success('First entry logged! Keep pushing!');
      } else {
        toast.success('Logged! Keep working towards that PR.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log');
    }
  };

  const createMovement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/movements`, movementFormData);
      toast.success('Movement created!');
      setShowMovementForm(false);
      setMovementFormData({ name: '', unit: 'kg', lower_is_better: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const deleteMovement = async (id) => {
    try {
      await axios.delete(`${API}/movements/${id}`);
      toast.success('Movement deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const startEditMovement = (mov) => { setEditMovementId(mov.id); setEditMovementName(mov.name); };

  const saveEditMovement = async () => {
    try {
      await axios.put(`${API}/movements/${editMovementId}`, { name: editMovementName });
      toast.success('Movement updated!');
      setEditMovementId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  const loggedMovementIds = myPRs.map((p) => p.movement.id);
  const unloggedMovements = movements.filter((m) => !loggedMovementIds.includes(m.id));
  const selectedMovUnit = movements.find((m) => m.id === logFormData.movement_id)?.unit || '';

  return (
    <div className="space-y-6" data-testid="pr-tracking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold uppercase tracking-tight text-[#FAFAFA] flex items-center gap-3">
            <Trophy className="w-8 h-8 text-[#CCFF00]" />
            Personal Records
          </h2>
          <p className="text-sm text-[#A1A1AA] mt-1">Track your best performances</p>
        </div>
        <div className="flex gap-2">
          {movements.length > 0 && (
            <Button
              onClick={() => openLogDialog(movements[0]?.id || '')}
              data-testid="log-pr-button"
              className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log PR
            </Button>
          )}
          {isCoachRole(user.role) && (
            <Button
              onClick={() => setShowMovementForm(true)}
              data-testid="add-movement-button"
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Movement
            </Button>
          )}
        </div>
      </div>

      {/* PR Cards */}
      {myPRs.length === 0 && unloggedMovements.length === 0 ? (
        <Card className="bg-[#18181B] border-[#27272A] p-8" data-testid="no-prs">
          <p className="text-[#A1A1AA] text-center text-lg">
            {movements.length === 0
              ? 'No movements created yet. Ask your coach to add movements to track.'
              : 'No PRs logged yet. Start tracking your progress!'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {myPRs.map((item) => {
            const mov = item.movement;
            const isExpanded = expandedMovement === mov.id;
            const bestLog = item.logs.find((l) => l.is_pr) || item.logs[0];
            return (
              <Card key={mov.id} className="bg-[#18181B] border-[#27272A] overflow-hidden" data-testid={`pr-card-${mov.id}`}>
                <button
                  onClick={() => setExpandedMovement(isExpanded ? null : mov.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#1F1F23] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-[#CCFF00]/10 p-2 rounded-sm">
                      <Trophy className="w-5 h-5 text-[#CCFF00]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA]">{mov.name}</h3>
                      <p className="text-xs text-[#A1A1AA]">{item.total_entries} {item.total_entries === 1 ? 'entry' : 'entries'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-[#A1A1AA] uppercase">Current PR</p>
                      <p className="text-xl font-heading font-black text-[#CCFF00]">
                        {formatValue(bestLog, mov.unit)}
                      </p>
                    </div>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-[#A1A1AA]" /> : <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-[#27272A]">
                    <div className="space-y-2 mt-3">
                      {item.logs.map((log) => (
                        <div key={log.id} className="flex items-center justify-between p-2.5 bg-[#09090B] rounded-sm border border-[#27272A]">
                          <div className="flex items-center gap-2 flex-wrap">
                            {log.is_pr && <span className="text-xs bg-[#CCFF00] text-black font-bold px-1.5 py-0.5 rounded-sm uppercase">PR</span>}
                            {/* Rx / Scaled badge */}
                            {log.rx === false
                              ? <span className="text-xs bg-[#A1A1AA]/20 text-[#A1A1AA] font-bold px-1.5 py-0.5 rounded-sm uppercase">Scaled</span>
                              : <span className="text-xs bg-[#CCFF00]/20 text-[#CCFF00] font-bold px-1.5 py-0.5 rounded-sm uppercase">Rx</span>
                            }
                            <div>
                              <span className="text-[#FAFAFA] font-semibold">{formatValue(log, mov.unit)}</span>
                              {log.notes && <p className="text-xs text-[#52525B]">{log.notes}</p>}
                            </div>
                          </div>
                          <span className="text-xs text-[#A1A1AA] flex-shrink-0">{new Date(log.date).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                    {/* Coach edit/delete */}
                    {isCoachRole(user.role) && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#27272A]">
                        {editMovementId === mov.id ? (
                          <>
                            <Input value={editMovementName} onChange={(e) => setEditMovementName(e.target.value)} className="bg-[#09090B] border-[#27272A] text-white h-8 text-sm flex-1" />
                            <Button onClick={saveEditMovement} size="sm" className="bg-[#CCFF00] text-black h-7 text-xs">Save</Button>
                            <Button onClick={() => setEditMovementId(null)} size="sm" variant="outline" className="h-7 text-xs border-[#27272A] text-[#A1A1AA]">Cancel</Button>
                          </>
                        ) : (
                          <>
                            <Button onClick={() => startEditMovement(mov)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-[#CCFF00] text-xs"><Edit className="w-3 h-3 mr-1" /> Rename</Button>
                            <Button onClick={() => deleteMovement(mov.id)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-[#EF4444] text-xs"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          {/* Unlogged movements */}
          {unloggedMovements.map((mov) => (
            <Card key={mov.id} className="bg-[#18181B]/50 border-[#1F1F23] p-4 flex items-center justify-between" data-testid={`unlogged-${mov.id}`}>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-[#52525B]" />
                <div>
                  {editMovementId === mov.id ? (
                    <div className="flex items-center gap-2">
                      <Input value={editMovementName} onChange={(e) => setEditMovementName(e.target.value)} className="bg-[#09090B] border-[#27272A] text-white h-8 text-sm w-40" />
                      <Button onClick={saveEditMovement} size="sm" className="bg-[#CCFF00] text-black h-7 text-xs">Save</Button>
                      <Button onClick={() => setEditMovementId(null)} size="sm" variant="outline" className="h-7 text-xs border-[#27272A] text-[#A1A1AA]">X</Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-base font-heading font-bold uppercase text-[#A1A1AA]">{mov.name}</h3>
                      <p className="text-xs text-[#52525B]">{mov.unit} {mov.lower_is_better ? '(lower is better)' : ''}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => openLogDialog(mov.id)} size="sm" className="bg-[#CCFF00] text-black font-bold uppercase text-xs hover:bg-[#B3E600]">Log</Button>
                {isCoachRole(user.role) && (
                  <>
                    <Button onClick={() => startEditMovement(mov)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-[#CCFF00]"><Edit className="w-3 h-3" /></Button>
                    <Button onClick={() => deleteMovement(mov.id)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ---- Log PR Dialog ---- */}
      <Dialog open={showLogForm} onOpenChange={setShowLogForm}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="log-pr-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Log Performance</DialogTitle>
          </DialogHeader>
          <form onSubmit={logPR} className="space-y-4">
            {/* Movement selector */}
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Movement</Label>
              <select
                data-testid="pr-movement-select"
                value={logFormData.movement_id}
                onChange={(e) => setLogFormData((f) => ({ ...f, movement_id: e.target.value, scoreSubtype: 'completed', mins: '', secs: '', repsCompleted: '', tiebreakMins: '', tiebreakSecs: '', rounds: '', extraReps: '', value: '' }))}
                required
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none"
              >
                {movements.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                ))}
              </select>
            </div>

            {/* Dynamic score inputs */}
            <PRScoreInput unit={selectedMovUnit} form={logFormData} upd={updLogField} />

            {/* Rx / Scaled */}
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Performance</Label>
              <RxToggle value={logFormData.rx} onChange={(v) => updLogField('rx', v)} />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Notes / Modifications (optional)</Label>
              <Textarea
                data-testid="pr-notes-input"
                value={logFormData.notes}
                onChange={(e) => updLogField('notes', e.target.value)}
                placeholder="e.g., Felt strong today"
                rows={2}
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLogForm(false)} className="border-[#27272A] text-[#A1A1AA]">Cancel</Button>
              <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Log</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Create Movement Dialog (Coach only) ---- */}
      <Dialog open={showMovementForm} onOpenChange={setShowMovementForm}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="create-movement-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Create Movement</DialogTitle>
          </DialogHeader>
          <form onSubmit={createMovement} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Name</Label>
              <Input
                data-testid="movement-name-input"
                value={movementFormData.name}
                onChange={(e) => setMovementFormData({ ...movementFormData, name: e.target.value })}
                required placeholder="e.g., Back Squat 1RM"
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Score Type / Unit</Label>
              <select
                data-testid="movement-unit-select"
                value={movementFormData.unit}
                onChange={(e) => {
                  const unit = e.target.value;
                  const lower = ['seconds', 'minutes', 'for_time'].includes(unit);
                  setMovementFormData({ ...movementFormData, unit, lower_is_better: lower });
                }}
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none"
              >
                <option value="kg">kg (Weight)</option>
                <option value="lbs">lbs (Weight)</option>
                <option value="for_time">For Time (completed vs. capped)</option>
                <option value="amrap">AMRAP (rounds + reps)</option>
                <option value="reps">Reps</option>
                <option value="seconds">Seconds (simple time)</option>
                <option value="minutes">Minutes</option>
                <option value="meters">Meters</option>
                <option value="calories">Calories</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="lower-better"
                checked={movementFormData.lower_is_better}
                onChange={(e) => setMovementFormData({ ...movementFormData, lower_is_better: e.target.checked })}
                className="rounded border-[#27272A]"
              />
              <Label htmlFor="lower-better" className="text-sm text-[#A1A1AA]">Lower is better (for time-based movements)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMovementForm(false)} className="border-[#27272A] text-[#A1A1AA]">Cancel</Button>
              <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PR Celebration popup */}
      {celebration && (
        <PRCelebration
          open={!!celebration}
          onDismiss={() => setCelebration(null)}
          movementName={celebration.movementName}
          value={celebration.value}
          unit={celebration.unit}
        />
      )}
    </div>
  );
}
